import { CssBaseline } from "@mui/material";
import { lighten } from "@mui/material/styles";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import * as React from "react";

type LayerThemeProviderProps = {
  children: React.ReactNode;
};

declare module "@mui/material/styles" {
  interface TypographyVariants {
    title1: React.CSSProperties;
    title2: React.CSSProperties;
    title3: React.CSSProperties;
    title4: React.CSSProperties;
    large: React.CSSProperties;
    regular: React.CSSProperties;
    small: React.CSSProperties;
    mini: React.CSSProperties;
    micro: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    title1?: React.CSSProperties;
    title2?: React.CSSProperties;
    title3?: React.CSSProperties;
    title4?: React.CSSProperties;
    large?: React.CSSProperties;
    regular?: React.CSSProperties;
    small?: React.CSSProperties;
    mini?: React.CSSProperties;
    micro?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    h1: false;
    h2: false;
    h3: false;
    h4: false;
    h5: false;
    h6: false;
    subtitle1: false;
    subtitle2: false;
    body1: false;
    body2: false;
    title1: true;
    title2: true;
    title3: true;
    title4: true;
    large: true;
    regular: true;
    small: true;
    mini: true;
    micro: true;
  }
}

const Palette = {
  PRIMARY: "#A855F7",
  SECONDARY: "#41425B",
  SUCCESS: "#06B6D4",
  WARNING: "#F59E0B",
  ERROR: "#EC4899",
};

const fontFamily =
  "'niveau-grotesk', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'";

const theme = createTheme({
  typography: {
    fontFamily,
    h1: undefined,
    h2: undefined,
    h3: undefined,
    h4: undefined,
    h5: undefined,
    h6: undefined,
    subtitle1: undefined,
    subtitle2: undefined,
    body1: undefined,
    body2: undefined,
    title1: {
      fontSize: 36,
      fontFamily,
    },
    title2: {
      fontSize: 24,
      fontFamily,
    },
    title3: {
      fontSize: 22,
      fontFamily,
    },
    title4: {
      fontSize: 20,
      fontFamily,
    },
    large: {
      fontSize: 18,
      fontFamily,
    },
    regular: {
      fontSize: 15,
      fontFamily,
    },
    small: {
      fontSize: 14,
      fontFamily,
    },
    mini: {
      fontSize: 12,
      fontFamily,
    },
    micro: {
      fontSize: 11,
      fontFamily,
    },
  },
  palette: {
    mode: "dark",
    primary: {
      main: Palette.PRIMARY,
      contrastText: "#FAF5FF",
    },
    secondary: {
      main: Palette.SECONDARY,
      contrastText: "#E6E6EF",
    },
    success: {
      main: Palette.SUCCESS,
      contrastText: "#083344",
    },
    error: {
      main: Palette.ERROR,
      contrastText: "#E6E6EF",
    },
    warning: {
      main: Palette.WARNING,
      contrastText: "#083344",
    },
  },
  components: {
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#FAFAFF",
          borderRadius: 4,
          border: "1px solid transparent",

          "&:disabled": {
            opacity: 0.5,
          },
        },
        sizeLarge: {
          padding: 8,
          fontSize: 16,
        },
        sizeMedium: {
          padding: 6,
          fontSize: 16,
        },
        sizeSmall: {
          padding: 6,
          fontSize: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          lineHeight: 1.25,
          textTransform: "capitalize",
          borderRadius: 6,
          fontWeight: 600,
        },
        contained: {
          border: "1px solid transparent",
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: lighten(Palette.PRIMARY, 0.2),
          },

          "&:disabled": {
            backgroundColor: Palette.PRIMARY,
            opacity: 0.5,
          },
        },
        containedSecondary: {
          border: "1px solid rgba(82, 82, 111, 0.5)",

          "&:hover": {
            backgroundColor: lighten(Palette.SECONDARY, 0.2),
          },

          "&:disabled": {
            backgroundColor: Palette.SECONDARY,
            opacity: 0.5,
          },
        },
        containedSuccess: {
          "&:hover": {
            backgroundColor: lighten(Palette.SUCCESS, 0.2),
          },

          "&:disabled": {
            backgroundColor: Palette.SUCCESS,
            opacity: 0.5,
          },
        },
        containedWarning: {
          "&:hover": {
            backgroundColor: lighten(Palette.WARNING, 0.2),
          },

          "&:disabled": {
            backgroundColor: Palette.WARNING,
            opacity: 0.5,
          },
        },
        containedError: {
          "&:hover": {
            backgroundColor: lighten(Palette.ERROR, 0.2),
          },

          "&:disabled": {
            backgroundColor: Palette.ERROR,
            opacity: 0.5,
          },
        },
        textSecondary: {
          color: "#E6E6EF",
        },
        outlinedSecondary: {
          color: "#E6E6EF",
        },
        sizeLarge: {
          padding: "4px 8px",
          fontSize: 14,
          minHeight: 32,
          height: "100%",
        },
        sizeMedium: {
          padding: "4px 8px",
          fontSize: 14,
          minHeight: 28,
          height: "100%",
        },
        sizeSmall: {
          padding: "2px 4px",
          fontSize: 12,
          minHeight: 24,
          height: "100%",
        },
        startIcon: {
          paddingLeft: 4,

          "& >*:nth-of-type(1)": {
            fontSize: "inherit",
          },
        },
        endIcon: {
          paddingRight: 4,

          "& >*:nth-of-type(1)": {
            fontSize: "inherit",
          },
        },
        iconSizeSmall: {
          fontSize: 12,
        },
        iconSizeMedium: {
          fontSize: 16,
        },
        iconSizeLarge: {
          fontSize: 18,
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variant: "regular",
      },
    },
  },
});

export const LayerThemeProvider = ({ children }: LayerThemeProviderProps) => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};
